import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Card, Typography, Spin } from 'antd';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../../api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const { Text } = Typography;

interface FastestByEventRow {
  event_name: string;
  gender: string;
  best_numeric: number;
  best_mark: string;
  sample_size: number;
}

interface QualifyingSource {
  competition: string;
  source_name: string;
  source_url: string;
}

interface QualifyingMark {
  gender: string;
  event_name: string;
  asian_games_mark: string;
  commonwealth_games_mark: string;
}

interface EventGenderValueRow<T> {
  event_name: string;
  women: T;
  men: T;
}

interface QualifyingMarksTableRow {
  event_name: string;
  commonwealth_w: string;
  asian_w: string;
  asian_m: string;
  commonwealth_m: string;
}

interface QualifierCountByEventTableRow {
  event_name: string;
  commonwealth_w: number | string;
  asian_w: number | string;
  asian_m: number | string;
  commonwealth_m: number | string;
}

/** One row per event with women on the left and men on the right. */
export function fastestRowsByEvent(rows: FastestByEventRow[]): EventGenderValueRow<string>[] {
  const byEvent = new Map<string, { M?: FastestByEventRow; W?: FastestByEventRow }>();
  for (const row of rows) {
    const current = byEvent.get(row.event_name) ?? {};
    if (row.gender === 'M') current.M = row;
    if (row.gender === 'W') current.W = row;
    byEvent.set(row.event_name, current);
  }
  const emDash = '—';
  return Array.from(byEvent.entries())
    .map(([event_name, { M, W }]) => ({
      event_name,
      women: W ? W.best_mark : emDash,
      men: M ? M.best_mark : emDash,
    }))
    .sort((a, b) => a.event_name.localeCompare(b.event_name));
}

/** One row per event for five-column qualifying-marks table layout. */
export function qualifyingMarksRowsByEvent(marks: QualifyingMark[]): QualifyingMarksTableRow[] {
  const byEvent = new Map<string, { M?: QualifyingMark; W?: QualifyingMark }>();
  for (const m of marks) {
    const cur = byEvent.get(m.event_name) ?? {};
    if (m.gender === 'M') cur.M = m;
    if (m.gender === 'W') cur.W = m;
    byEvent.set(m.event_name, cur);
  }
  const emDash = '—';
  return Array.from(byEvent.entries())
    .map(([event_name, { M, W }]) => ({
      event_name,
      commonwealth_w: W?.commonwealth_games_mark ?? emDash,
      asian_w: W?.asian_games_mark ?? emDash,
      asian_m: M?.asian_games_mark ?? emDash,
      commonwealth_m: M?.commonwealth_games_mark ?? emDash,
    }))
    .sort((a, b) => a.event_name.localeCompare(b.event_name));
}

interface QualifiersByEventRow {
  event_name: string;
  gender: string;
  asian_games_qualified: number;
  commonwealth_games_qualified: number;
}

/** One row per event for five-column qualifiers table layout. */
export function qualifierCountRowsByEvent(rows: QualifiersByEventRow[]): QualifierCountByEventTableRow[] {
  const byEvent = new Map<
    string,
    { M?: Pick<QualifiersByEventRow, 'asian_games_qualified' | 'commonwealth_games_qualified'>; W?: Pick<QualifiersByEventRow, 'asian_games_qualified' | 'commonwealth_games_qualified'> }
  >();
  for (const row of rows) {
    const current = byEvent.get(row.event_name) ?? {};
    if (row.gender === 'M') {
      current.M = {
        asian_games_qualified: row.asian_games_qualified,
        commonwealth_games_qualified: row.commonwealth_games_qualified,
      };
    }
    if (row.gender === 'W') {
      current.W = {
        asian_games_qualified: row.asian_games_qualified,
        commonwealth_games_qualified: row.commonwealth_games_qualified,
      };
    }
    byEvent.set(row.event_name, current);
  }
  const emDash = '—';
  return Array.from(byEvent.entries())
    .map(([event_name, { M, W }]) => ({
      event_name,
      commonwealth_w: W?.commonwealth_games_qualified ?? emDash,
      asian_w: W?.asian_games_qualified ?? emDash,
      asian_m: M?.asian_games_qualified ?? emDash,
      commonwealth_m: M?.commonwealth_games_qualified ?? emDash,
    }))
    .sort((a, b) => a.event_name.localeCompare(b.event_name));
}

interface Props {
  /** Pass 'SGP' on the Singapore tab to filter relevant charts. Omit for global. */
  countryFilter?: string;
}

const LoadingCard: React.FC<{ title: string; height?: number }> = ({ title, height = 300 }) => (
  <Card title={title} size="small">
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spin tip="Loading…" />
    </div>
  </Card>
);

const NoDataCard: React.FC<{ title: string }> = ({ title }) => (
  <Card title={title} size="small">
    <Text type="secondary">No data available.</Text>
  </Card>
);

const EventGenderTable = <T extends string | number>({
  title,
  rows,
  ariaLabel,
}: {
  title: string;
  rows: EventGenderValueRow<T>[];
  ariaLabel: string;
}) => {
  const th = {
    padding: '8px 10px',
    textAlign: 'center' as const,
    fontWeight: 600,
    borderBottom: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
  };
  const td = { padding: '6px 10px', textAlign: 'center' as const };
  const tdEvent = { ...td, fontWeight: 500 };

  return (
    <Card title={title} size="small">
      <div style={{ overflowX: 'auto' }}>
        <table aria-label={ariaLabel} style={{ width: '100%', borderCollapse: 'collapse', minWidth: 460 }}>
          <thead>
            <tr>
              <th style={th}>Women</th>
              <th style={th}>Event</th>
              <th style={th}>Men</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.event_name}>
                <td style={td}>{String(row.women)}</td>
                <td style={tdEvent}>{row.event_name}</td>
                <td style={td}>{String(row.men)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const FiveColumnEventTable = <T extends QualifyingMarksTableRow | QualifierCountByEventTableRow>({
  rows,
  ariaLabel,
}: {
  rows: T[];
  ariaLabel: string;
}) => {
  const th = {
    padding: '8px 10px',
    textAlign: 'center' as const,
    fontWeight: 600,
    borderBottom: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
  };
  const td = { padding: '6px 10px', textAlign: 'center' as const };
  const tdEvent = { ...td, fontWeight: 500 };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table aria-label={ariaLabel} style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
        <thead>
          <tr>
            <th style={th}>Commonwealth Games Women</th>
            <th style={th}>Asian Games Women</th>
            <th style={th}>Events</th>
            <th style={th}>Asian Games Men</th>
            <th style={th}>Commonwealth Games Men</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.event_name}>
              <td style={td}>{row.commonwealth_w}</td>
              <td style={td}>{row.asian_w}</td>
              <td style={tdEvent}>{row.event_name}</td>
              <td style={td}>{row.asian_m}</td>
              <td style={td}>{row.commonwealth_m}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------

const FastestByEvent: React.FC<{ countryFilter?: string }> = ({ countryFilter }) => {
  const [data, setData] = useState<FastestByEventRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getAuthHeaders().then((headers) => {
      const params = countryFilter ? { country: countryFilter } : {};
      return axios.get(`${API_BASE_URL}/api/analytics/sea-fastest-by-event`, { headers, params });
    }).then((res) => {
      if (!cancelled) setData(res.data || []);
    }).catch((e) => console.error('sea-fastest-by-event:', e))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [countryFilter]);

  if (loading) return <LoadingCard title="Fastest Mark in Southeast Asia (Last 12 Months)" />;
  if (!data.length) return <NoDataCard title="Fastest Mark in Southeast Asia (Last 12 Months)" />;

  return <EventGenderTable title="Fastest Mark in Southeast Asia (Last 12 Months)" rows={fastestRowsByEvent(data)} ariaLabel="Fastest marks by event with women and men columns" />;
};

const QualifiersByEvent: React.FC = () => {
  const [data, setData] = useState<QualifiersByEventRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getAuthHeaders().then((headers) =>
      axios.get(`${API_BASE_URL}/api/analytics/sea-qualifiers-by-event`, { headers })
    ).then((res) => {
      if (!cancelled) setData(res.data || []);
    }).catch((e) => console.error('sea-qualifiers-by-event:', e))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingCard title="SEA Athletes Meeting Asian/Commonwealth Qualifying Marks" height={360} />;
  if (!data.length) return <NoDataCard title="SEA Athletes Meeting Asian/Commonwealth Qualifying Marks" />;

  return (
    <Card title="SEA Athletes Meeting Asian/Commonwealth Qualifying Marks" size="small">
      <FiveColumnEventTable
        rows={qualifierCountRowsByEvent(data)}
        ariaLabel="Qualified athlete counts by event for Asian and Commonwealth standards"
      />
    </Card>
  );
};

const QualifyingMarksTable: React.FC = () => {
  const [data, setData] = useState<QualifyingMark[]>([]);
  const [sources, setSources] = useState<QualifyingSource[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getAuthHeaders().then((headers) =>
      axios.get(`${API_BASE_URL}/api/analytics/qualifying-marks`, { headers })
    ).then((res) => {
      if (!cancelled) {
        setData(res.data?.marks || []);
        setSources(res.data?.sources || []);
      }
    }).catch((e) => console.error('qualifying-marks:', e))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingCard title="Asian & Commonwealth Qualifying Marks" height={260} />;
  if (!data.length) return <NoDataCard title="Asian & Commonwealth Qualifying Marks" />;

  const rows = qualifyingMarksRowsByEvent(data);

  return (
    <Card title="Asian & Commonwealth Qualifying Marks" size="small">
      <FiveColumnEventTable
        rows={rows}
        ariaLabel="Qualifying standards: Asian Games and Commonwealth Games marks by event"
      />
      <div style={{ marginTop: 10 }}>
        {sources.map((s) => (
          <div key={`${s.competition}-${s.source_url}`}>
            <Text type="secondary">
              {s.competition}:{' '}
              <a href={s.source_url} target="_blank" rel="noreferrer">
                {s.source_name}
              </a>
            </Text>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------

/**
 * Self-loading analytics charts for the Dashboard.
 * Pass countryFilter="SGP" on the Singapore tab to filter where applicable.
 * Each chart fetches independently so none blocks another.
 */
const AnalyticsCharts: React.FC<Props> = ({ countryFilter }) => (
  <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
    <FastestByEvent countryFilter={countryFilter} />
    {!countryFilter && <QualifiersByEvent />}
    {!countryFilter && <QualifyingMarksTable />}
  </div>
);

export default AnalyticsCharts;
